import type { Huddle } from '@avslack/shared';

/**
 * Egyetlen esemeny, aminek a valos ideju retegtol a huddle-vezerlohoz kell
 * eljutnia: "a szerver kileptetett".
 *
 * Kulon kis busz, mert a ket reteg kozott nincs szulo-gyerek kapcsolat, es egy
 * ilyen ritka esemeny miatt nem eri meg a React-fat atrendezni.
 */
type EvictedHandler = (huddleId: string, reason: string) => void;

const evictedHandlers = new Set<EvictedHandler>();

export function onHuddleEvicted(handler: EvictedHandler): () => void {
  evictedHandlers.add(handler);
  return () => evictedHandlers.delete(handler);
}

export function emitHuddleEvicted(huddleId: string, reason: string): void {
  for (const handler of evictedHandlers) handler(huddleId, reason);
}

// ─── A hivas allapotanak frissulese ───────────────────────────────────────────

/**
 * A szerver frissitett huddle-allapota.
 *
 * **Miert kell ez a kerulout?** A hivas sajat ablakban fut, a valos ideju
 * kapcsolatot pedig a `RealtimeProvider` tartja - a `HuddleProvider` viszont
 * sajat allapotban orzi a hivast. A ketto kozott nincs szulo-gyerek kapcsolat,
 * ezert egy kis buszon szolunk at.
 *
 * Enelkul a hivas allapota csak az eletjelnel (20 masodperc) frissulne: a
 * nemitas ikonja kesve jelenne meg, es az elutasitott hivas percekig ugy
 * latszana, mintha meg csorogne. Egy hivasban a husz masodperc nagyon hosszu.
 */
type UpdatedHandler = (huddle: Huddle) => void;

const updatedHandlers = new Set<UpdatedHandler>();

export function onHuddleUpdated(handler: UpdatedHandler): () => void {
  updatedHandlers.add(handler);
  return () => updatedHandlers.delete(handler);
}

export function emitHuddleUpdated(huddle: Huddle): void {
  for (const handler of updatedHandlers) handler(huddle);
}

// ─── Behivas ──────────────────────────────────────────────────────────────────

export interface HuddleInvite {
  huddle: Huddle;
  invitedByName: string;
  channelName: string | null;
  /** Ment-e rola push is - akkor a feliratkozott eszkoz nem jelez melle. */
  pushed?: boolean;
}

type InviteHandler = (invite: HuddleInvite) => void;

const inviteHandlers = new Set<InviteHandler>();

export function onHuddleInvited(handler: InviteHandler): () => void {
  inviteHandlers.add(handler);
  return () => inviteHandlers.delete(handler);
}

export function emitHuddleInvited(invite: HuddleInvite): void {
  for (const handler of inviteHandlers) handler(invite);
}

// ─── A behivas visszavonasa ───────────────────────────────────────────────────

/**
 * A hivas megszunt, mielott felvettek volna.
 *
 * Enelkul a hivott oldalan a csengohang addig szol, amig a sajat idozitoje le
 * nem jar - a hivo mar rege kilepett, es a masik meg mindig egy nem letezo
 * hivast hall. Ez ugyanaz a "nem jelez helyesen" hiba, ami miatt a projekt
 * elindult, csak a masik iranybol.
 */
type CancelledHandler = (huddleId: string) => void;

const cancelledHandlers = new Set<CancelledHandler>();

export function onHuddleCancelled(handler: CancelledHandler): () => void {
  cancelledHandlers.add(handler);
  return () => cancelledHandlers.delete(handler);
}

export function emitHuddleCancelled(huddleId: string): void {
  for (const handler of cancelledHandlers) handler(huddleId);
}
